import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { CreateRequest, RequestCategory, type RequestDetail } from '@syndiqia/comms/contracts';
import { errorMessage } from '../api';
import { useCommsMutation, useMyUnits, useRequests } from '../comms-queries';
import { Alert, Disclosure, Empty, Field, Loading, PageHeader, validate, type FieldErrors } from '../components/ui';
import { useI18n } from '../i18n';
import { useParties, useUnits } from '../queries';
import { useSyndicate } from '../session';

export function RequestsPage() {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const [filter, setFilter] = useState<'open' | 'all'>('open');
  const requests = useRequests(filter);
  const units = useUnits();
  const parties = useParties();
  const manages = s.can('requests.manage');
  const statuses = manages ? t.requests.statusesBoard : t.requests.statuses;

  return (
    <>
      <PageHeader
        title={t.requests.title}
        intro={s.inboundAddress ? (s.can('requests.manage') ? t.requests.byEmailBoard(s.inboundAddress) : t.requests.byEmail(s.inboundAddress)) : undefined}
        actions={<Disclosure label={t.requests.new}>{(close) => <NewRequest onDone={close} />}</Disclosure>} />
      <div className="toolbar">
        <nav className="subnav subnav-inline">
          {(['open', 'all'] as const).map((f) => (
            <button key={f} type="button" className={f === filter ? 'active' : undefined} onClick={() => setFilter(f)}>
              {t.requests.filters[f]}
            </button>
          ))}
        </nav>
      </div>
      {requests.isPending && <Loading label={t.loading} />}
      {requests.isError && <Alert>{errorMessage(requests.error, t)}</Alert>}
      {requests.data?.length === 0 && <Empty>{t.requests.empty}</Empty>}
      {!!requests.data?.length && (
        <table className="table table-stack">
          <thead>
            <tr>
              <th>{t.requests.number}</th>
              <th>{t.requests.subject}</th>
              <th>{t.requests.unit}</th>
              {manages && <th>{t.requests.requester}</th>}
              <th>{t.requests.status}</th>
              <th>{t.requests.updated}</th>
            </tr>
          </thead>
          <tbody>
            {requests.data.map((r) => (
              <tr key={r.id} className={r.priority === 'urgent' && r.status !== 'resolved' ? 'row-alert' : undefined}>
                <td className="nowrap hide-sm">#{r.number}</td>
                <td className="cell-title">
                  <span className="show-sm">#{r.number} </span>
                  <Link to={`${s.base}/requests/${r.id}`}>{r.subject}</Link>
                  <div className="muted small">
                    {t.requests.categories[r.category]}
                    {r.priority === 'urgent' && ` · ${t.requests.urgent}`}
                  </div>
                </td>
                <td data-label={t.requests.unit}>{r.unitId ? units.data?.find((u) => u.id === r.unitId)?.label : '—'}</td>
                {manages && <td data-label={t.requests.requester}>{parties.data?.find((p) => p.id === r.requesterPartyId)?.displayName ?? '—'}</td>}
                <td data-label={t.requests.status}>
                  <span className={`badge badge-status-${r.status}`}>{statuses[r.status]}</span>
                </td>
                <td className="nowrap muted" data-label={t.requests.updated}>{date(r.updatedAt.slice(0, 10))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function NewRequest({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const s = useSyndicate();
  const navigate = useNavigate();
  const manages = s.can('requests.manage');
  const units = useUnits();
  const myUnits = useMyUnits();
  const parties = useParties();
  const [values, setValues] = useState({ category: 'maintenance', urgent: false, unitId: '', requesterPartyId: '', subject: '', description: '' });
  const [errors, setErrors] = useState<FieldErrors>({});
  const create = useCommsMutation<CreateRequest, RequestDetail>((api, base, input) => api(`${base}/requests`, { method: 'POST', body: input }));

  const selectableUnits = manages ? (units.data ?? []) : (units.data ?? []).filter((u) => myUnits.data?.includes(u.id));
  const set = (key: keyof typeof values) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [key]: e.target.value }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const result = validate(CreateRequest, {
      category: values.category,
      priority: values.urgent ? 'urgent' : 'normal',
      subject: values.subject,
      description: values.description,
      unitId: values.unitId,
      requesterPartyId: values.requesterPartyId,
    });
    setErrors(result.errors ?? {});
    if (result.data) create.mutate(result.data, { onSuccess: (r) => (onDone(), navigate(`${s.base}/requests/${r.id}`)) });
  };

  return (
    <form onSubmit={submit} className="form" noValidate>
      <div className="row">
        <Field label={t.requests.category} error={errors.category}>
          <select value={values.category} onChange={set('category')}>
            {RequestCategory.options.map((c) => (
              <option key={c} value={c}>
                {t.requests.categories[c]}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t.requests.unit} error={errors.unitId}>
          <select value={values.unitId} onChange={set('unitId')}>
            <option value="">{t.requests.noUnit}</option>
            {selectableUnits.map((u) => (
              <option key={u.id} value={u.id}>
                {u.label}
              </option>
            ))}
          </select>
        </Field>
        {manages && (
          <Field label={t.requests.onBehalfOf} error={errors.requesterPartyId}>
            <select value={values.requesterPartyId} onChange={set('requesterPartyId')}>
              <option value="">{t.requests.myself}</option>
              {parties.data?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.displayName}
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>
      <Field label={t.requests.subject} error={errors.subject}>
        <input value={values.subject} onChange={set('subject')} autoFocus />
      </Field>
      <Field label={t.requests.description} hint={t.requests.descriptionHint} error={errors.description}>
        <textarea rows={5} value={values.description} onChange={set('description')} />
      </Field>
      <label className="inline-field">
        <input type="checkbox" checked={values.urgent} onChange={(e) => setValues((v) => ({ ...v, urgent: e.target.checked }))} />
        {t.requests.urgent}
      </label>
      {create.isError && <Alert>{errorMessage(create.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={create.isPending}>
          {t.requests.send}
        </button>
        <button type="button" className="button" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}
